import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore, UserRole } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import NotificationCenter from '../Common/NotificationCenter';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { user, refreshUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const [notificationOpen, setNotificationOpen] = React.useState(false);

  // Fetch fresh user data when component mounts (only if we have a token but no user, or user data seems stale)
  useEffect(() => {
    // Only refresh if we have authentication but no user data, or if user data is missing critical fields
    // This prevents overwriting correct user data with potentially wrong data from a stale token
    if (!user || !user.email || !user.username) {
      refreshUser();
    }
  }, [refreshUser, user]);

  const navItems = [
    {
      path: '/dashboard',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100',
    },
    {
      path: '/packages',
      icon: 'fas fa-box',
      label: 'Packages',
      adminOnly: true,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      hoverBg: 'hover:bg-emerald-100',
    },
    {
      path: '/forms',
      icon: 'fas fa-file-alt',
      label: 'Pool Customers',
      hideFromAccountManager: true, // Hide from Account Managers
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100',
    },
    {
      path: '/impact-plan',
      icon: 'fas fa-project-diagram',
      label: 'Impact Plan',
      hideFromAccountManager: true, // Hide from Account Manager (Sales)
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100',
    },
    {
      path: '/sales',
      icon: 'fas fa-chart-bar',
      label: 'Sales',
      salesAccess: true, // Only Account Managers, Admins, and Super Admins can access
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      hoverBg: 'hover:bg-green-100',
    },
    {
      path: '/usage',
      icon: 'fas fa-chart-area',
      label: 'Data Usage',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      hoverBg: 'hover:bg-amber-100',
    },
    {
      path: '/migrations',
      icon: 'fas fa-code-branch',
      label: 'Migrations',
      superAdminOnly: true,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      hoverBg: 'hover:bg-cyan-100',
    },
    {
      path: '/powerbi',
      icon: 'fas fa-chart-line',
      label: 'Power BI',
      adminOnly: true,
      superAdminOnly: true,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100',
    },
    {
      path: '/xero',
      icon: 'fas fa-dollar-sign',
      label: 'Xero Monitor',
      adminOnly: true,
      superAdminOnly: true,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      hoverBg: 'hover:bg-emerald-100',
    },
    {
      path: '/users',
      icon: 'fas fa-users',
      label: 'User Management',
      adminOnly: true,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      hoverBg: 'hover:bg-indigo-100',
    },
    {
      path: '/service-accounts',
      icon: 'fas fa-robot',
      label: 'Service Accounts',
      adminOnly: true,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      hoverBg: 'hover:bg-cyan-100',
    },
    {
      path: '/usage-events',
      icon: 'fas fa-paper-plane',
      label: 'Usage Events',
      adminOnly: true,
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      hoverBg: 'hover:bg-sky-100',
    },
    {
      path: '/monitor',
      icon: 'fas fa-satellite',
      label: 'Starlink Monitor',
      hideFromAccountManager: true, // Hide from Account Managers
      monitorAccess: true, // Only Account Managers, Admins, and Super Admins can access
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      hoverBg: 'hover:bg-teal-100',
    },
  ];

  return (
    <div
      className={`bg-white shadow-xl border-r border-gray-100 w-64 flex flex-col z-50 transition-all duration-300 transform 
        fixed inset-y-0 left-0 md:relative md:transform-none md:min-h-screen overflow-hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
    >
      {/* Close button for mobile */}
      <button
        className="md:hidden absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-all duration-200 z-10"
        onClick={onClose}
        aria-label="Close sidebar"
      >
        <i className="fas fa-times text-lg"></i>
      </button>

      {/* Logo Section */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-center rounded-2xl p-4 border border-gray-100 bg-gradient-to-r from-slate-50 to-indigo-50 shadow-sm">
          <div className="relative group">
            {/* Glow layer (blinking) */}
            <div className="absolute -inset-2 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse bg-indigo-600/40"></div>

            {/* Logo container (blinking) */}
            <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ring-4 ring-offset-4 ring-offset-transparent ring-indigo-600/20 animate-pulse bg-white">
            <img 
              src="/logo.png" 
              alt="Aura Group" 
                className="w-12 h-12 object-contain drop-shadow"
              onError={(e) => {
                // Fallback to database icon if logo fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl items-center justify-center hidden shadow-md">
                <i className="fas fa-database text-white text-2xl"></i>
              </div>
            </div>

            {/* Online indicator */}
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Actions (below logo) */}
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-3 py-2 shadow-sm">
          <div className="flex items-center">
            <NotificationCenter
              isOpen={notificationOpen}
              onClose={() => setNotificationOpen(false)}
              onToggle={() => setNotificationOpen(!notificationOpen)}
            />
          </div>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-red-50 hover:text-red-700 transition-all"
            onClick={() => {
              logout();
              toast.success('Logged out successfully');
              navigate('/login');
            }}
            title="Logout"
          >
            <i className="fas fa-sign-out-alt"></i>
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="space-y-2 pb-4">
          {navItems
            .filter((item) => {
              // Super Admin-only pages must stay hidden from every other role, including auditors.
              if (item.superAdminOnly && !user?.isSuperAdmin && user?.role !== UserRole.SUPER_ADMIN) return false;

              // Auditors can see all pages (view-only access)
              if (user?.hasAuditAccess) return true;
              
              // Filter out admin-only items if user doesn't have admin access
              if (item.adminOnly && !user?.hasAdminAccess) return false;
              // Filter out super admin-only items if user isn't super admin
              if (item.superAdminOnly && !user?.isSuperAdmin && user?.role !== UserRole.SUPER_ADMIN) return false;
              // Filter out sales access items if user doesn't have sales access
              if (item.salesAccess && !user?.hasAdminAccess && user?.role !== UserRole.ACCOUNT_MANAGER) return false;
              // Filter out monitor access items if user doesn't have monitor access
              if (item.monitorAccess && !user?.hasAdminAccess && user?.role !== UserRole.ACCOUNT_MANAGER) return false;
              // Hide items from Account Managers if specified
              if (item.hideFromAccountManager && user?.role === UserRole.ACCOUNT_MANAGER) return false;
              return true;
            })
            .map((item) => (
            <div key={item.path} className="relative">
              <NavLink
                to={item.path}
                onClick={() => {
                  // Auto-close sidebar on mobile when navigation item is clicked
                  if (onClose && window.innerWidth < 768) {
                    onClose();
                  }
                }}
                className={({ isActive }) =>
                  `flex items-center px-3 py-3 text-sm font-semibold rounded-2xl transition-all duration-200 group relative overflow-hidden ${
                    isActive
                      ? `bg-gradient-to-r from-slate-900 to-indigo-900 text-white shadow-lg border border-slate-800`
                      : `text-gray-700 hover:bg-gray-100 border border-transparent hover:border-gray-200`
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Icon container */}
                    <div className={`relative flex items-center justify-center w-11 h-11 rounded-xl mr-3 transition-all duration-200 ${
                      isActive 
                        ? 'bg-white/10 shadow-md' 
                        : `bg-gray-50 group-hover:bg-white group-hover:shadow-sm`
                    }`}>
                      <i className={`${item.icon} text-lg ${isActive ? 'text-white' : 'text-[#4169E1]'} transition-transform duration-200 group-hover:scale-105`}></i>
                    </div>
                    
                    {/* Label */}
                    <span className="relative font-semibold">{item.label}</span>
                    
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-2 h-2 bg-white/90 rounded-full shadow-md"></div>
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            </div>
          ))}
        </div>
      </nav>

      {/* User (bottom) */}
      <div className="px-4 pb-4">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-3 border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
              <i className="fas fa-user text-white text-sm"></i>
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-gray-900 font-bold text-sm truncate">{user?.username || 'User'}</p>
              <p className="text-gray-600 text-xs flex items-center truncate">
                {user?.role === UserRole.SUPER_ADMIN ? (
                  <>
                    <i className="fas fa-crown text-red-500 mr-1 text-xs"></i>
                    Super Administrator
                  </>
                ) : user?.role === UserRole.ADMIN ? (
                  <>
                    <i className="fas fa-crown text-yellow-500 mr-1 text-xs"></i>
                    Administrator
                  </>
                ) : user?.role === UserRole.ACCOUNT_MANAGER ? (
                  <>
                    <i className="fas fa-user-tie text-blue-500 mr-1 text-xs"></i>
                    Account Manager
                  </>
                ) : user?.role === UserRole.CUSTOMER_SUPPORT ? (
                  <>
                    <i className="fas fa-headset text-green-500 mr-1 text-xs"></i>
                    Customer Support
                  </>
                ) : user?.role === UserRole.AUDITOR ? (
                  <>
                    <i className="fas fa-search text-purple-500 mr-1 text-xs"></i>
                    Auditor
                  </>
                ) : (
                  <>
                    <i className="fas fa-user text-gray-500 mr-1 text-xs"></i>
                    Monitor Manager
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100">
        <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
          <div className="text-gray-500 text-xs space-y-1">
            <p className="font-semibold flex items-center justify-center">
              <i className="fas fa-copyright text-blue-500 mr-1"></i>
              2026 Aura Group
            </p>
            <p className="flex items-center justify-center">
              <i className="fas fa-code text-blue-500 mr-1"></i>
              Data Management System v2.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 
